import { apiClient } from './api';
import type {
  PlatformPermissionCatalogGroup,
  PlatformRoleCatalogItem,
} from '@erpgold/shared';

export type PlatformAccountRole = string;
export type PlatformAccountStatus = 'active' | 'inactive' | 'locked';

export interface PlatformAccount {
  id: string;
  email: string;
  name: string;
  role: PlatformAccountRole;
  status: PlatformAccountStatus;
  permissions: string[];
  last_login_at?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface PlatformUserFilters {
  page?: number;
  limit?: number;
  search?: string;
  role?: string;
  status?: string;
}

export interface PlatformUsersResponse {
  data: PlatformAccount[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface CreatePlatformUserDto {
  email: string;
  name: string;
  role: PlatformAccountRole;
  status?: PlatformAccountStatus;
  permissions?: string[];
  password?: string;
}

export interface UpdatePlatformUserDto {
  email?: string;
  name?: string;
  role?: PlatformAccountRole;
  status?: PlatformAccountStatus;
  permissions?: string[];
  password?: string;
}

export interface CreatePlatformRoleDto {
  key: string;
  label: string;
  description?: string;
  default_permissions?: string[];
}

export interface UpdatePlatformRoleDto {
  label?: string;
  description?: string;
  default_permissions?: string[];
}

interface Envelope<T> {
  success?: boolean;
  data: T;
}

interface RawPlatformUser {
  id?: string;
  _id?: string;
  email?: string;
  name?: string;
  role?: string;
  status?: string;
  permissions?: string[];
  last_login_at?: string | null;
  created_at?: string;
  updated_at?: string;
}

const unwrap = <T>(payload: Envelope<T> | T): T => {
  if (payload && typeof payload === 'object' && 'data' in (payload as Record<string, unknown>)) {
    return (payload as Envelope<T>).data;
  }
  return payload as T;
};

const toPlatformUser = (raw: RawPlatformUser): PlatformAccount => ({
  id: raw.id || raw._id || '',
  email: raw.email || '',
  name: raw.name || '',
  role: (raw.role || 'support_admin') as PlatformAccountRole,
  status: (raw.status || 'active') as PlatformAccountStatus,
  permissions: raw.permissions || [],
  last_login_at: raw.last_login_at,
  created_at: raw.created_at,
  updated_at: raw.updated_at,
});

export const platformUserService = {
  async getUsers(filters: PlatformUserFilters = {}): Promise<PlatformUsersResponse> {
    const response = await apiClient.get<Envelope<PlatformUsersResponse> | PlatformUsersResponse>('/users', {
      params: filters,
    });

    const payload = unwrap(response.data);
    const items = (payload.data || []).map(toPlatformUser);

    return {
      data: items,
      meta: payload.meta || {
        page: filters.page || 1,
        limit: filters.limit || 20,
        total: items.length,
        totalPages: 1,
      },
    };
  },

  async getUserById(id: string): Promise<PlatformAccount> {
    const response = await apiClient.get<Envelope<RawPlatformUser> | RawPlatformUser>(`/users/${id}`);
    return toPlatformUser(unwrap(response.data));
  },

  async createUser(data: CreatePlatformUserDto): Promise<PlatformAccount> {
    const response = await apiClient.post<Envelope<RawPlatformUser> | RawPlatformUser>('/users', data);
    return toPlatformUser(unwrap(response.data));
  },

  async updateUser(id: string, data: UpdatePlatformUserDto): Promise<PlatformAccount> {
    const response = await apiClient.put<Envelope<RawPlatformUser> | RawPlatformUser>(`/users/${id}`, data);
    return toPlatformUser(unwrap(response.data));
  },

  async deleteUser(id: string): Promise<void> {
    await apiClient.delete(`/users/${id}`);
  },

  async getRoleCatalog(): Promise<PlatformRoleCatalogItem[]> {
    const response = await apiClient.get<Envelope<PlatformRoleCatalogItem[]> | PlatformRoleCatalogItem[]>('/users/roles');
    return unwrap(response.data);
  },

  async getPermissionCatalog(): Promise<PlatformPermissionCatalogGroup[]> {
    const response = await apiClient.get<
      Envelope<PlatformPermissionCatalogGroup[]> | PlatformPermissionCatalogGroup[]
    >('/users/permissions');
    return unwrap(response.data);
  },

  async createRole(data: CreatePlatformRoleDto): Promise<PlatformRoleCatalogItem> {
    const response = await apiClient.post<Envelope<PlatformRoleCatalogItem> | PlatformRoleCatalogItem>('/users/roles', data);
    return unwrap(response.data);
  },

  async updateRole(id: string, data: UpdatePlatformRoleDto): Promise<PlatformRoleCatalogItem> {
    const response = await apiClient.put<Envelope<PlatformRoleCatalogItem> | PlatformRoleCatalogItem>(`/users/roles/${id}`, data);
    return unwrap(response.data);
  },

  async deleteRole(id: string): Promise<void> {
    await apiClient.delete(`/users/roles/${id}`);
  },
};

export default platformUserService;
